import React, { useState } from "react";
import { X, Loader2, Type, Youtube, List, FileText } from "lucide-react";
import { createDemoVideoAPI } from "../services/apicollection";

const DemoVideoForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: "",
    iframeUrl: "",
    topics: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const parseTopics = (str) => {
    if (!str || typeof str !== "string") return [];
    return str
      .split(/[\n,]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.iframeUrl?.trim()) {
      alert("YouTube iframe URL is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title?.trim() || "Demo Video",
        iframeUrl: formData.iframeUrl.trim(),
        topics: parseTopics(formData.topics),
        description: formData.description?.trim() || "",
      };
      const res = await createDemoVideoAPI(payload);
      if (!res?.status) {
        alert(res?.message || "Failed to create demo video");
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to create demo video";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-200">
            Add Demo Video
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Add a YouTube unlisted video to display on the About page. Paste the
            iframe embed URL.
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#13233a] text-slate-500 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-400">
            Title (optional)
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <Type size={16} className="text-slate-400" />
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="h-7 w-full border-none bg-transparent text-sm outline-none"
              placeholder="e.g. Lecture Demo - Python Basics"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            YouTube iframe URL <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <Youtube size={16} className="text-slate-400" />
            <input
              type="text"
              value={formData.iframeUrl}
              onChange={(e) => handleChange("iframeUrl", e.target.value)}
              className="h-7 w-full border-none bg-transparent text-sm outline-none"
              placeholder="https://www.youtube.com/embed/VIDEO_ID or paste full iframe code"
              required
            />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Paste the embed URL (e.g. https://www.youtube.com/embed/xxx) or the
            full iframe HTML. Works with unlisted videos.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Topics covered
          </label>
          <div className="mt-1 flex items-start gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <List size={16} className="mt-0.5 shrink-0 text-slate-400" />
            <textarea
              value={formData.topics}
              onChange={(e) => handleChange("topics", e.target.value)}
              rows={3}
              className="w-full flex-1 resize-none border-none bg-transparent text-sm outline-none"
              placeholder="One topic per line or comma-separated&#10;e.g. Python basics, Variables, Loops"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400">
            Description
          </label>
          <div className="mt-1 flex items-start gap-2 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus-within:border-slate-500 focus-within:ring-1 focus-within:ring-slate-500">
            <FileText size={16} className="mt-0.5 shrink-0 text-slate-400" />
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full flex-1 resize-none border-none bg-transparent text-sm outline-none"
              placeholder="Brief description of what this demo video covers..."
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-[#0d1b2e]"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-700 disabled:opacity-60"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Add Demo Video
          </button>
        </div>
      </form>
    </div>
  );
};

export default DemoVideoForm;
